import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { tarifasApi, errMsg, fmtDate, fmtMoney, tierLabel } from "./api";

const PriceRows = ({ rows, empty }) => (
  <div className="overflow-x-auto">
    {rows.length === 0 && <p className="text-sm text-muted-foreground py-3">{empty}</p>}
    {rows.length > 0 && (
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr className="text-left">
            <th className="py-1 pr-2 font-medium">Fecha</th><th className="pr-2 font-medium">Proveedor</th><th className="pr-2 font-medium">Actividad proveedor</th>
            <th className="pr-2 font-medium">Actividad KimKim</th><th className="pr-2 font-medium">Tramo</th><th className="pr-2 font-medium text-right">Neto</th>
            <th className="pr-2 font-medium text-right">+8 %</th><th className="pr-2 font-medium text-right">Verificado</th><th className="pr-2 font-medium text-right">Anterior</th>
            <th className="pr-2 font-medium">Archivo</th><th className="font-medium">Estado</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.id} className="border-t" data-testid={`registry-row-${h.id}`}>
              <td className="py-1 pr-2 whitespace-nowrap">{fmtDate(h.logged_at)}</td>
              <td className="pr-2">{h.provider_name} <span className="text-muted-foreground">({h.alias})</span></td>
              <td className="pr-2">{h.provider_activity_name}{h.provider_code ? ` [${h.provider_code}]` : ""}</td>
              <td className="pr-2">{h.kimkim_title} <a href={h.kimkim_url} target="_blank" rel="noreferrer" className="inline-block align-middle text-muted-foreground"><ExternalLink className="h-3 w-3" /></a></td>
              <td className="pr-2 whitespace-nowrap">{tierLabel(h.min, h.max)} · {h.unit === "per_person" ? "pp" : "grupo"}{h.season ? ` · ${h.season}` : ""}</td>
              <td className="pr-2 text-right font-mono">{fmtMoney(h.net, "")}</td>
              <td className="pr-2 text-right font-mono">{fmtMoney(h.computed, "")}</td>
              <td className="pr-2 text-right font-mono">{fmtMoney(h.verified, "")}</td>
              <td className="pr-2 text-right font-mono text-muted-foreground">{fmtMoney(h.previous, "")}</td>
              <td className="pr-2">{h.file_link ? <a href={h.file_link} target="_blank" rel="noreferrer" className="underline">{h.file_name}</a> : h.file_name}</td>
              <td><Badge className={`rounded-md ${h.status === "updated" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"} hover:bg-inherit`}>{h.status === "updated" ? "actualizado" : "fallido"}</Badge>{h.extras ? <span className="block text-muted-foreground">{h.extras}</span> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export const Registry = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await tarifasApi.registry());
    } catch (e) {
      toast.error(errMsg(e, "No se pudo cargar el registro"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Card className="rounded-md" data-testid="registry-card">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-base font-bold">Registro de correspondencias y tarifas</h3>
          <p className="text-xs text-muted-foreground">Documento interno de la aplicación. Distingue el precio calculado del precio realmente verificado en KimKim.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="registry-refresh" size="sm" variant="outline" className="rounded-md" onClick={load} disabled={loading}>{loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}</Button>
          <Button data-testid="registry-export" size="sm" className="rounded-md" asChild>
            <a href={tarifasApi.exportUrl} download><Download className="h-3 w-3 mr-1" /> Descargar Excel</a>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {!data ? <p className="text-sm text-muted-foreground py-3">Cargando…</p> : (
          <Tabs defaultValue="current">
            <TabsList className="rounded-md mb-3">
              <TabsTrigger value="correspondences" data-testid="registry-tab-correspondences" className="rounded-md">Correspondencias ({data.correspondences.length})</TabsTrigger>
              <TabsTrigger value="current" data-testid="registry-tab-current" className="rounded-md">Tarifas actuales ({data.current.length})</TabsTrigger>
              <TabsTrigger value="history" data-testid="registry-tab-history" className="rounded-md">Historial ({data.history.length})</TabsTrigger>
              <TabsTrigger value="pending" data-testid="registry-tab-pending" className="rounded-md">Pendientes y errores ({data.pending.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="correspondences">
              {data.correspondences.length === 0 && <p className="text-sm text-muted-foreground py-3">Aún no hay correspondencias confirmadas.</p>}
              <div className="divide-y">
                {data.correspondences.map((c) => (
                  <div key={c.id} className="py-2 text-sm flex flex-wrap items-center gap-2" data-testid={`registry-corr-${c.id}`}>
                    <span className="text-muted-foreground text-xs">{c.provider} ({c.alias}) · {[c.country, c.destination].filter(Boolean).join("/")}</span>
                    <span className="font-medium">{c.provider_name}</span>{c.provider_code && <span className="font-mono text-xs">[{c.provider_code}]</span>}
                    <span className="text-muted-foreground">→</span>
                    <span className="font-medium">{c.kimkim_title}</span>
                    <a href={c.kimkim_url} target="_blank" rel="noreferrer" className="text-muted-foreground"><ExternalLink className="h-3 w-3" /></a>
                    <span className="text-xs text-muted-foreground">id {c.kimkim_activity_id} · {c.decided_by || "IA"} · {fmtDate(c.updated_at)}</span>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="current"><PriceRows rows={data.current} empty="Todavía no se ha actualizado ningún precio en KimKim." /></TabsContent>
            <TabsContent value="history"><PriceRows rows={data.history} empty="Sin historial." /></TabsContent>
            <TabsContent value="pending">
              {data.pending.length === 0 && <p className="text-sm text-muted-foreground py-3">No hay pendientes ni errores.</p>}
              <div className="divide-y">
                {data.pending.map((p, i) => (
                  <div key={i} className="py-2 text-sm flex flex-wrap items-center gap-2" data-testid={`registry-pending-${i}`}>
                    <Badge className="rounded-md bg-amber-100 text-amber-800 hover:bg-amber-100">{p.type}</Badge>
                    <span className="text-xs text-muted-foreground">{p.provider}</span>
                    <span className="font-medium">{p.item}</span>
                    {p.kimkim_title && <span className="text-muted-foreground">→ {p.kimkim_title}</span>}
                    <span className="w-full text-xs text-muted-foreground">{p.status} · {p.detail}</span>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};
